import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {Component, computed, effect, ElementRef, inject, PLATFORM_ID, Signal, signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle} from '@angular/material/expansion';
import {ActivatedRoute, Router} from '@angular/router';
import {HelpSection} from '@models//help-content';
import {HelpService} from '@services/help/help.service';
import {WindowService} from '@services/window/window.service';

@Component({
  standalone: true,
  selector: 'app-help',
  templateUrl: './help.component.html',
  styleUrl: './help.component.scss',
  imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle],
})
export class HelpComponent {
  public helpService = inject(HelpService);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public windowService = inject(WindowService);

  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);

  private readonly elementRef = inject(ElementRef);

  private readonly params = toSignal(this.route.params);

  activeSectionSlug: Signal<string> = computed(() => this.params()?.['section-slug']);
  activeSubsectionSlug: Signal<string> = computed(() => this.params()?.['subsection-slug']);
  activeTertiarySectionSlug: Signal<string> = computed(() => this.params()?.['tertiary-section-slug']);

  constructor() {
    effect(() => {
      const sections = this.helpService.visibleHelpSections();
      const sectionSlug = this.activeSectionSlug();
      const subsectionSlug = this.activeSubsectionSlug();
      const tertiarySectionSlug = this.activeTertiarySectionSlug();

      if (sections && sections.length > 0 && (sectionSlug || subsectionSlug || tertiarySectionSlug)) {
        if (isPlatformBrowser(this.platformId)) {
          this.scrollToActivePanel();
        }
      }
    });
  }

  // Uses a hierarchical selector to prevent slug collisions when scrolling.
  scrollToActivePanel(): void {
    const sectionSlug = this.activeSectionSlug();
    const subsectionSlug = this.activeSubsectionSlug();
    const tertiarySectionSlug = this.activeTertiarySectionSlug();

    let selector = '';

    if (tertiarySectionSlug && subsectionSlug && sectionSlug) {
      // Hierarchical selector: mat-expansion-panel[section] mat-expansion-panel[subsection] mat-expansion-panel[tertiary]
      selector = `mat-expansion-panel[data-slug="${sectionSlug}"] mat-expansion-panel[data-slug="${subsectionSlug}"] mat-expansion-panel[data-slug="${tertiarySectionSlug}"]`;
    } else if (subsectionSlug && sectionSlug) {
      // Hierarchical selector: mat-expansion-panel[section] mat-expansion-panel[subsection]
      selector = `mat-expansion-panel[data-slug="${sectionSlug}"] mat-expansion-panel[data-slug="${subsectionSlug}"]`;
    } else if (sectionSlug) {
      // Selector for main section panel: mat-expansion-panel[section]
      selector = `mat-expansion-panel[data-slug="${sectionSlug}"]`;
    }

    if (!selector) {
      return;
    }

    const targetElement: HTMLElement | null = this.document.querySelector(selector);

    if (targetElement && isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 350);
    } else if (isPlatformBrowser(this.platformId)) {
      console.warn(`[scrollToActivePanel] No target element found for scrolling selector: ${selector}`);
    }
  }

  // Use manual state tracking if route-based expansion is not the sole source of truth.
  public expandedSections = signal(new Set<string>());

  // Checks if the active route is this section or a child section within it.
  isSectionExpanded(section: HelpSection): boolean {
    if (this.expandedSections().has(section.slug)) {
      return true;
    }

    const activeSectionSlug = this.activeSectionSlug();
    const activeSubsectionSlug = this.activeSubsectionSlug();
    const activeTertiarySlug = this.activeTertiarySectionSlug();

    // Only proceed if this section matches the active top-level slug
    if (section.slug !== activeSectionSlug) {
      return false;
    }

    // If the active route is /section-slug (direct match)
    if (!activeSubsectionSlug && !activeTertiarySlug) {
      return true;
    }

    // If the active route is /section-slug/subsection-slug, check if the subsection exists.
    if (activeSubsectionSlug && !activeTertiarySlug) {
      return section.subsections?.some(sub => sub.slug === activeSubsectionSlug) ?? false;
    }

    // If the active route is /section-slug/subsection-slug/tertiary-slug, check if the tertiary exists.
    if (activeTertiarySlug) {
      return (
        section.subsections?.some(sub => sub.subsections?.some(tertiary => tertiary.slug === activeTertiarySlug)) ??
        false
      );
    }

    return false;
  }

  isSubsectionExpanded(sectionSlug: string, subsection: HelpSection): boolean {
    if (this.expandedSections().has(`${sectionSlug}-${subsection.slug}`)) {
      return true;
    }

    const activeSectionSlug = this.activeSectionSlug();
    const activeSubsectionSlug = this.activeSubsectionSlug();
    const activeTertiarySlug = this.activeTertiarySectionSlug();

    // Ensure that the parent section slug matches the active route segment.
    if (sectionSlug !== activeSectionSlug) {
      return false;
    }

    // This subsection must match the active subsection slug.
    if (subsection.slug === activeSubsectionSlug) {
      return true;
    }

    // If a tertiary section is active, check if it's a child of this subsection.
    if (activeTertiarySlug) {
      return subsection.subsections?.some(tertiary => tertiary.slug === activeTertiarySlug) ?? false;
    }

    return false;
  }

  isTertiarySectionExpanded(sectionSlug: string, subsectionSlug: string, tertiary: HelpSection): boolean {
    if (this.expandedSections().has(`${sectionSlug}-${subsectionSlug}-${tertiary.slug}`)) {
      return true;
    }

    const activeSectionSlug = this.activeSectionSlug();
    const activeSubsectionSlug = this.activeSubsectionSlug();
    const activeTertiarySlug = this.activeTertiarySectionSlug();

    // Match all three slugs from the active route
    return (
      tertiary.slug === activeTertiarySlug &&
      subsectionSlug === activeSubsectionSlug &&
      sectionSlug === activeSectionSlug
    );
  }

  toggleSection(slug: string, isExpanded: boolean): void {
    this.expandedSections.update(set => {
      const next = new Set(set);
      if (isExpanded) {
        next.add(slug);
      } else {
        next.delete(slug);
      }
      return next;
    });
  }
}
